import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import LoginModal from '../components/LoginModal'; // Assuming the path to LoginModal
import api from "../api/axiosInstance";

const AdminContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    background: linear-gradient(135deg, #f8f9fa, #ffffff);
    border-radius: 12px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h1`
    text-align: center;
    color: #333;
    margin-bottom: 30px;
    font-size: 28px;
    font-weight: 700;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    width: 100%;
`;

const UserTable = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-top: 20px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    table-layout: fixed;
`;

const TableHeader = styled.th`
    background: linear-gradient(135deg, #f56607, #ff8c00);
    color: white;
    padding: 15px;
    text-align: left;
    font-weight: 600;
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    word-break: break-word;
`;

const TableRow = styled.tr`
    transition: all 0.3s ease;
    &:nth-child(even) {
        background-color: #f9f9f9;
    }
    &:hover {
        background-color: #e0e0e0;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    &.premium-row {
        background-color: #fff3e0;
        &:nth-child(even) {
            background-color: #ffe9c9;
        }
        &:hover {
            background-color: #ffdab3;
        }
    }
`;

const TableCell = styled.td`
    padding: 15px;
    border-bottom: 1px solid #eee;
    font-size: 15px;
    color: #444;
    word-break: break-word;
`;

const PremiumBadge = styled.span`
    background: linear-gradient(135deg, #ffd700, #ffcc00);
    color: #333;
    padding: 4px 8px;
    border-radius: 12px;
    font-weight: bold;
    font-size: 12px;
    text-transform: uppercase;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const NoPremium = styled.span`
    color: #888;
    font-style: italic;
`;

const NoData = styled.p`
  text-align: center;
  color: #666;
  font-style: italic;
  font-size: 16px;
  margin-top: 20px;
`;

const Admin: React.FC = () => {
    const dispatch = useDispatch();
    const { user, isAuthenticated } = useSelector((state: any) => state.auth); // Assuming authSlice has user and isAuthenticated
    const [isModalOpen, setIsModalOpen] = useState(!isAuthenticated);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isAuthenticated && user?.isAdmin) {
            fetchLastUsers();
        } else if (isAuthenticated && !user?.isAdmin) {
            setError('Nemáte administrátorské práva.');
        } else {
            setIsModalOpen(true);
        }
    }, [isAuthenticated, user]);

    const fetchLastUsers = async () => {
        try {
            const response = await api.get('/account/last30');
            setUsers(response.data);
        } catch (err) {
            setError('Nepodarilo sa načítať používateľov.');
            console.error(err);
        }
    };

    const handleLogin = (data: any) => {
        // LoginModal already dispatches loginSuccess, so state updates automatically
        setIsModalOpen(false);
    };

    return (
        <AdminContainer>
            <Title>Admin Panel - Posledných 30 Používateľov</Title>

            {error && <NoData>{error}</NoData>}

            {isModalOpen ? (
                <LoginModal
                    isOpen={isModalOpen}
                    onRequestClose={() => setIsModalOpen(false)}
                    onLogin={handleLogin}
                />
            ) : (
                users.length > 0 ? (
                    <TableWrapper>
                        <UserTable>
                            <thead>
                            <tr>
                                <TableHeader>Meno</TableHeader>
                                <TableHeader>Email</TableHeader>
                                <TableHeader>Je Premium</TableHeader>
                                <TableHeader>Dátum Registrácie</TableHeader>
                                <TableHeader>Zdroj Registrácie</TableHeader>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map((user: any, index: number) => (
                                <TableRow key={index} className={user.isPremium ? 'premium-row' : ''}>
                                    <TableCell>{user.fullName}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        {user.isPremium ? (
                                            <PremiumBadge>Premium</PremiumBadge>
                                        ) : (
                                            <NoPremium>Nie</NoPremium>
                                        )}
                                    </TableCell>
                                    <TableCell>{new Date(user.registrationDate).toLocaleDateString('sk-SK')}</TableCell>
                                    <TableCell>{user.referralSource || 'Priamy'}</TableCell>
                                </TableRow>
                            ))}
                            </tbody>
                        </UserTable>
                    </TableWrapper>
                ) : (
                    <NoData>Žiadni používatelia nenájdení.</NoData>
                )
            )}
        </AdminContainer>
    );
};

export default Admin;